import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, controlToastState } from "risa-oasis-ui_v2";
import { v4 as uuidv4 } from "uuid";
import { checkPatientEligibility } from "../../api/postCall/checkEvBV";
import { getEvBvReportDetails } from "../../api/postCall/getEvBvReport";
import DragDropUpload from "../../components/dragDropUpload/dragDropUpload";
import { fetchNycbsMedicalPaOrdersFromFirebase } from "../../redux/slice/ordersSlice/nycbsMedicalPaSlice";
import { AppDispatch } from "../../redux/store/store";
import { InfoIcon } from "../../svg/info-icon";
import { logError } from "../../utils/customLogger";
import EligibilityTable from "./components/eligibilityTable/eligibilityTable";

const generateShortId = () => uuidv4().replace(/-/g, "").slice(0, 15);

const EligibilityCheck = () => {
  const [fileListLength, setFileListLength] = useState(0);
  const [uniqueId, setUniqueId] = useState(generateShortId());
  const [downloadedURL, setDownloadedURL] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [reportDownload, setReportDownload] = useState(false);
  const [operationSuccess, setOperationSuccess] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    fileListLength === 0 && setUniqueId(generateShortId());
    setReportDownload(false);
    setOperationSuccess(false);
  }, [fileListLength]);

  useEffect(() => {
    dispatch(fetchNycbsMedicalPaOrdersFromFirebase());
  }, []);

  const checkPatientEligibilityViaApi = async () => {
    setLoading(true);
    setReportDownload(false);
    const storageRef = `gs://prior--backen-prod-svc-u4g8.appspot.com/eligibility_check/${uniqueId}`;

    const body = {
      url: storageRef,
      org_id: "nycbs_medical_pa",
      job_id: uniqueId,
    };

    const response = await checkPatientEligibility(body);
    if (response.hasError) {
      controlToastState("check-eligibility-failure");
    } else {
      controlToastState("check-eligibility-success");
      fetchReportDetails();
      setReportDownload(true);
    }
    setLoading(false);
  };

  const fetchReportDetails = async () => {
    const body = {
      org_id: "nycbs_medical_pa",
      job_id: uniqueId,
    };
    const response = await getEvBvReportDetails(body);
    if (response.hasError) {
      logError(response.response, "Error while fetching report details");
    }
    setOperationSuccess(true);
  };

  return (
    <div className="eligibility-check flex h-full w-full flex-col gap-4 overflow-auto p-4">
      <div className="eligibility-check-header text-h11 font-bold">
        Check Patients Eligibility
      </div>
      <div className="eligibility-check-body flex flex-col gap-4">
        <div className="eligibility-check-body__content text-body font-regular">
          Please upload a file or drag and drop a file here to check the
          eligibility of patients. (only .csv file)
        </div>
        <div className="eligibility-check-body__uploader">
          <DragDropUpload
            id={"ev-bv-check"}
            allowedFileTypes={[".csv"]}
            filePath={`/eligibility_check/${uniqueId}`}
            fileListLength={(length) => setFileListLength(length)}
            downloadedURL={(url) => setDownloadedURL(url)}
            onSuccessfullyUploaded={operationSuccess}
            maxFileSize={10 * 1024 * 1024}
          />
        </div>
        <div className="eligibility-check-body__button flex w-full justify-end gap-4">
          {loading && (
            <>
              <div className="eligibility-check--info mr-auto flex items-center gap-2 text-body font-normal italic">
                <InfoIcon />
                Checking the eligibility would take couple of minutes. Please do
                not refresh the page.
              </div>
            </>
          )}
          <Button
            disabled={
              downloadedURL.length === 0 ||
              downloadedURL[0] === "" ||
              loading ||
              reportDownload
                ? true
                : false
            }
            children={loading ? "Checking..." : "Check Eligibility"}
            onClick={checkPatientEligibilityViaApi}
            buttonType={"secondary"}
            size={"medium"}
          />
        </div>
      </div>
      <div className="eligibililty-check-table flex-1">
        <EligibilityTable />
      </div>
    </div>
  );
};

export default EligibilityCheck;
